/**
 * The field is `readOnly`, so the browser draws no caret of its own. These
 * helpers place a drawn one: the caret index lives in the input's own
 * `selectionStart`, and text is measured on a canvas, so nothing renders
 * and no layout is forced.
 */

let canvasContext: CanvasRenderingContext2D | null = null;

/** One shared offscreen canvas for every field. */
const getContext = (): CanvasRenderingContext2D | null => {
  if (!canvasContext) {
    canvasContext = document.createElement('canvas').getContext('2d');
  }
  return canvasContext;
};

interface FieldMetrics {
  /** Width of `text` as the field draws it, letter-spacing and text-transform included. */
  measure: (text: string) => number;
  /** Where the text starts, from the field's left border edge. */
  textLeft: number;
  /** Width of the area the text is shown in. */
  innerWidth: number;
}

/** Reads the field's font from its computed style - a style lookup only, no layout. */
const fieldMetrics = (field: HTMLInputElement): FieldMetrics | null => {
  const context = getContext();
  if (!context) {
    return null;
  }

  const style = window.getComputedStyle(field);
  context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  /** "normal" parses to NaN - no extra spacing. */
  const spacing = parseFloat(style.letterSpacing) || 0;
  const uppercase = style.textTransform === 'uppercase';
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingRight = parseFloat(style.paddingRight) || 0;

  return {
    measure: (text) =>
      context.measureText(uppercase ? text.toUpperCase() : text).width + spacing * text.length,
    textLeft: (parseFloat(style.borderLeftWidth) || 0) + paddingLeft,
    innerWidth: field.clientWidth - paddingLeft - paddingRight,
  };
};

/**
 * Moves `caret` to the field's caret index, scrolling the field so the caret
 * stays in view, and restarts the blink so it is solid right after a key -
 * swapping between two identical animations restarts it without a reflow.
 */
export const paintCaret = (field: HTMLInputElement, caret: HTMLElement): void => {
  const metrics = fieldMetrics(field);
  if (!metrics) {
    return;
  }

  const index = field.selectionStart ?? field.value.length;
  const x = metrics.measure(field.value.slice(0, index));

  let scroll = field.scrollLeft;
  if (x - scroll > metrics.innerWidth) {
    scroll = x - metrics.innerWidth;
  } else if (x < scroll) {
    scroll = x;
  }
  field.scrollLeft = scroll;

  /** Read back - the browser clamps it to what can actually scroll. */
  caret.style.transform = `translate3d(${metrics.textLeft + x - field.scrollLeft}px, 0, 0)`;
  caret.classList.toggle('is-blink-restart');
};

/** The caret index closest to a tap at `clientX`. */
export const caretIndexAt = (field: HTMLInputElement, clientX: number): number => {
  const metrics = fieldMetrics(field);
  const value = field.value;
  if (!metrics) {
    return value.length;
  }

  const x = clientX - field.getBoundingClientRect().left - metrics.textLeft + field.scrollLeft;

  let best = 0;
  let bestDistance = Math.abs(x);
  for (let index = 1; index <= value.length; index += 1) {
    const distance = Math.abs(metrics.measure(value.slice(0, index)) - x);
    if (distance > bestDistance) {
      /** Widths only grow from here on. */
      break;
    }
    best = index;
    bestDistance = distance;
  }
  return best;
};
